import { useState } from 'react'
import { Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useEmailReadsRefresh, useLogEmail } from '@/queries/clientEmails'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { MAX_EMAIL_LENGTH } from '@/types/clientEmails'

const COUNT_FROM = 90000

export function LogEmailDialog({ companyId, companyName }: { companyId: string; companyName: string }) {
  const [open, setOpen] = useState(false)
  const [subject, setSubject] = useState('')
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const log = useLogEmail(companyId)
  useEmailReadsRefresh(companyId)
  const length = text.trim().length
  const tooLong = length > MAX_EMAIL_LENGTH

  // Clear the draft only once it is logged: closing the dialog keeps it, so a stray click never loses a long paste.
  function clear() {
    setOpen(false)
    setSubject('')
    setText('')
    setError(null)
  }

  async function submit() {
    setError(null)
    try {
      await log.mutateAsync({ text: text.trim(), ...(subject.trim() && { subject: subject.trim() }) })
      clear()
    } catch (err) {
      setError(extractErrorMessage(err, "Couldn't log the email"))
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Mail />
          Log email
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Log an email</DialogTitle>
          <DialogDescription>{companyName}</DialogDescription>
        </DialogHeader>
        <p className="rounded-md bg-etn-accent-tint px-3 py-2 text-[12.5px] text-etn-text">Staff only. The client sees nothing unless someone creates a ticket from a request.</p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="log-email-subject">Subject (optional)</Label>
            <Input id="log-email-subject" value={subject} maxLength={200} onChange={e => setSubject(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="log-email-text">Paste the email or the whole thread</Label>
            <Textarea id="log-email-text" className="min-h-64" value={text} onChange={e => setText(e.target.value)} />
            {length > COUNT_FROM && (
              <p className={tooLong ? 'text-etn-micro text-etn-danger-text' : 'text-etn-micro text-etn-text-soft'}>
                {length.toLocaleString('en-GB')} of {MAX_EMAIL_LENGTH.toLocaleString('en-GB')} characters
              </p>
            )}
          </div>
          {error && (
            <p role="alert" className="text-sm text-etn-danger-text">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button disabled={!text.trim() || tooLong || log.isPending} onClick={submit}>
            Log email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
